import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { NotebookPen } from 'lucide-react';
import { useAuth } from '../context/useAuth';
import { hasGuestPrivileges } from '../utils/roles';
import { commitmentsAPI, profilesAPI } from '../api/client';
import PrayerOverview from '../components/PrayerOverview';
import GuidedTour from '../components/GuidedTour';
import ComingUpCard from '../components/ComingUpCard';
import DashboardActionBanner from '../components/DashboardActionBanner';
import SponsorBanner from '../components/SponsorBanner';
import AskIfComposer from '../components/AskIfComposer';
import SnapshotHomeCard from '../components/SnapshotHomeCard';
import HomeGrowGoalsCard from '../components/HomeGrowGoalsCard';
import type { Commitment } from '../types';

function timeOfDayGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const isGuest = hasGuestPrivileges(user);
  const [showTour, setShowTour] = useState(false);
  const [allCommitments, setAllCommitments] = useState<Commitment[]>([]);

  const loadCommitments = async () => {
    if (!user?.id) return;
    try {
      const rows = await commitmentsAPI.getAll();
      setAllCommitments(rows.filter((c: Commitment) => c.user === user.id));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  useEffect(() => {
    if (user && !user.has_seen_tour) {
      setShowTour(true);
    }
  }, [user]);

  const dismissTour = async () => {
    setShowTour(false);
    try {
      await profilesAPI.updateMyProfile({ has_seen_tour: true });
      if (refreshUser) await refreshUser();
    } catch {
      // Non-blocking: if persistence fails the tour just shows again next visit.
    }
  };

  useEffect(() => {
    if (!isGuest) loadCommitments();
  }, [user?.id, isGuest]);

  const handleCheckIn = async (id: number, completed: boolean) => {
    await commitmentsAPI.checkIn(id, completed);
    await loadCommitments();
  };

  const handleUncheckIn = async (id: number) => {
    await commitmentsAPI.uncheckIn(id);
    await loadCommitments();
  };

  const handleComplete = async (id: number) => {
    await commitmentsAPI.complete(id);
    await loadCommitments();
  };

  const handleUncomplete = async (id: number) => {
    await commitmentsAPI.uncomplete(id);
    await loadCommitments();
  };

  const firstName = user?.first_name || user?.username;

  return (
    <div className="space-y-6">
      {showTour && <GuidedTour onClose={dismissTour} />}

      <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-4 lg:flex-nowrap">
        <div className="shrink-0 md:basis-full lg:basis-auto lg:max-w-md">
          <h1 className="text-2xl md:text-3xl font-bold text-navy">
            {timeOfDayGreeting()}, {firstName}
          </h1>
          {user?.purpose_statement ? (
            <p className="mt-1 text-sm text-navy/70 italic">
              “{user.purpose_statement}”
            </p>
          ) : (
            !isGuest && (
              <Link
                to="/settings?tab=purpose"
                className="mt-1 inline-block text-sm text-gold hover:underline"
              >
                Set your purpose statement →
              </Link>
            )
          )}
          {user?.quarterly_goal ? (
            <p className="mt-1 text-sm text-gray-500">
              Season goal: {user.quarterly_goal}
            </p>
          ) : (
            !isGuest && (
              <Link
                to="/settings?tab=purpose"
                className="mt-1 block text-sm text-gray-400 hover:text-gold hover:underline"
              >
                Set a season goal →
              </Link>
            )
          )}
        </div>
        <AskIfComposer className="w-full md:flex-1" />
        {!isGuest && (
          <Link
            to="/notes?new=1"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-navy px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-navy-dark transition"
          >
            <NotebookPen size={16} /> Add note
          </Link>
        )}
      </div>

      <SponsorBanner />
      {!isGuest && <DashboardActionBanner />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:auto-rows-fr">
        <ComingUpCard />
        <SnapshotHomeCard />
        {!isGuest && (
          <HomeGrowGoalsCard
            commitments={allCommitments}
            onCheckIn={handleCheckIn}
            onUncheckIn={handleUncheckIn}
            onComplete={handleComplete}
            onUncomplete={handleUncomplete}
          />
        )}
        {!isGuest && <PrayerOverview />}
      </div>
    </div>
  );
}
